import { QToastProps } from '@qualio/ui-components';
import { AxiosError } from 'axios';
import { TFunction } from 'i18next';
import {
  MigrationDTO,
  MigrationFinishedStatusLabelMap,
  MigrationStatusValueEnum,
} from '../../../api/model/migration';
import {
  BULK_FILE_UPLOAD_IMPORT_AUTH_ERROR,
  logCustomError,
} from '../../../messages/LogErrorMessages';
import { getHttpError } from '../../../util/ErrorManagementUtils';
import {
  BULK_FILE_UPLOAD_FILES_LIMIT,
  checkFileExtensionMatchesAcceptedMimeTypes,
  FILE_UPLOAD_SIZE_LIMIT_MB,
} from '../../../util/FileUploadUtils';
import { delayedReload } from '../../../util/PageUtils';
import { RemoteFileMetadata } from '../QFilePicker/index';

export const validateFilesBeforeUpload = (
  fileList: File[] | RemoteFileMetadata[],
  currentBulkSize: number,
  showToast: (opts: QToastProps) => void,
  t: TFunction,
  singleFile = false,
  isExternal = false,
) => {
  const sizeTitle = singleFile
    ? t('fileUpload.failedToUploadFile')
    : t('fileUpload.failedToAddFiles');
  const sizeDescription = singleFile
    ? t('fileUpload.fileIs')
    : t('fileUpload.oneOrMoreFilesAre');

  if (!isExternal) {
    const hasFileOverSizeLimit = fileList.some(
      (file) =>
        file instanceof File &&
        file.size > FILE_UPLOAD_SIZE_LIMIT_MB * 1024 * 1024,
    );
    if (hasFileOverSizeLimit) {
      showToast({
        'data-cy': 'bulk-upload-size-limit-error-toast',
        id: 'bulk-upload-error',
        status: 'error',
        title: sizeTitle,
        description: t('fileUpload.overSizeLimit', {
          sizeDescription,
          limit: FILE_UPLOAD_SIZE_LIMIT_MB,
        }),
      });
      return false;
    }
  }

  if (!singleFile) {
    const hasInvalidFileAmount =
      fileList.length + currentBulkSize > BULK_FILE_UPLOAD_FILES_LIMIT;
    if (hasInvalidFileAmount) {
      showToast({
        'data-cy': 'bulk-upload-max-files-warning-toast',
        status: 'warning',
        title: t('fileUpload.maximumNumberOfFilesAdded'),
        description: t('fileUpload.maximumFilesPerBulkImport', {
          limit: BULK_FILE_UPLOAD_FILES_LIMIT,
        }),
        id: 'maximum-files-warning-toast',
      });
      return false;
    }
  }

  const hasInvalidFormat = fileList.some((file) => {
    let filename = '';
    if (file instanceof File) {
      filename = file.name;
    } else {
      filename = file.filename;
    }
    return !checkFileExtensionMatchesAcceptedMimeTypes(filename);
  });
  if (hasInvalidFormat) {
    showToast({
      'data-cy': 'bulk-upload-unsupported-format-error-toast',
      status: 'error',
      title: t('fileUpload.failedToAddFiles'),
      description: t('fileUpload.oneOrMoreUnsupportedFormat'),
      id: 'unsupported-file-format-error-toast',
    });
    return false;
  }

  return true;
};

export const isMigrationComplete = (m: MigrationDTO | undefined) =>
  (m && m.status in MigrationFinishedStatusLabelMap) ?? false;

export const getMigrationStatusCompleteUserMessage = (
  status: string,
  fileLength: number,
  t: TFunction,
): {
  message: QToastProps['description'];
  status: QToastProps['status'];
  title: QToastProps['title'];
} => {
  if (status === MigrationStatusValueEnum.COMPLETE) {
    return {
      title: t('success.importComplete'),
      message: t('success.successfullyImportedFiles', {
        fileLength,
        count: fileLength,
      }),
      status: 'success',
    };
  }
  if (status === MigrationStatusValueEnum.ERROR) {
    return {
      title: t('fileUpload.importFailed'),
      message: t('fileUpload.failedToImportFiles', {
        fileLength,
        count: fileLength,
      }),
      status: 'error',
    };
  }
  return {
    title: '',
    message: '',
    status: 'info',
  };
};

export const handleMigrationError = (
  error: unknown,
  message: string,
  showToast: (opts: QToastProps) => void,
  t: TFunction,
) => {
  if (error instanceof AxiosError && error.response?.status === 403) {
    showToast({
      'data-cy': 'bulk-upload-migration-warning-toast',
      title: t('notifications.outdatedUserInformation'),
      description: t('validation.activeCompanySwitched'),
      status: 'warning',
      id: `error-migration-toast`,
    });
    logCustomError(BULK_FILE_UPLOAD_IMPORT_AUTH_ERROR, { error });
    delayedReload();
    return;
  }
  return getHttpError(error, message);
};
